import {
  App,
  Button,
  Flex,
  Form,
  Image,
  Input,
  Segmented,
  Space,
  Tag,
  Typography,
  Upload
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'
import {
  Building2,
  Eye,
  LoaderCircle,
  Store,
  Trash2,
  Upload as UploadIcon,
  User,
  Users,
  type LucideIcon
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { CompanySizeEnum as CompanySize } from 'src/api/main'
import { AvatarWithInitials } from 'src/components'
import { MAX_SVG_MB, storedPreviewUrl } from 'src/utils'

import { COMPANY_SIZES, HANDLE_MIN_LENGTH } from '../constants'
import { useStyles } from '../styles'
import type { HandleAvailability } from '../useHandleAvailability'
import type { OnboardingState } from '../useOnboardingState'
import { LOGO_ACCEPT, LOGO_MAX_DIMENSION, validateLogo } from '../utils'

import { useFieldError } from './hooks'
import { StepHeader } from './StepHeader'

const { Text } = Typography

/**
 * One glyph per size bucket, read as a ladder: a single person, a team, a shop,
 * an office. Lucide has no headcount icons, so the escalation carries the
 * meaning rather than any one icon on its own.
 */
const COMPANY_SIZE_ICONS: Record<CompanySize, LucideIcon> = {
  '1-10': User,
  '11-50': Users,
  '51-200': Store,
  '201-1000': Building2
}

type Props = {
  state: OnboardingState
  photoFiles: UploadFile[]
  onPhotoFilesChange: (files: UploadFile[]) => void
  /** Host the workspace slug becomes a subdomain of, e.g. `ebsio.dev.example.net`. */
  baseHost: string
  /**
   * Resolved by the page, which also needs it to gate the step's action —
   * running the check here as well would duplicate the debounce.
   */
  availability: HandleAvailability
}

export const DetailsStep = ({
  state,
  photoFiles,
  onPhotoFilesChange,
  baseHost,
  availability
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const {
    draft,
    setName,
    setHandle,
    normalizeHandle,
    patch,
    focusProps,
    hoverProps
  } = state
  const fieldError = useFieldError(state.error)
  const handleSpotlight = focusProps('handle')

  const photo = photoFiles[0]
  const photoPreview = photo?.url ?? photo?.preview

  const onUploadChange: UploadProps['onChange'] = async (info) => {
    const next = info.fileList.slice(-1)
    const current = next[0]
    if (current?.originFileObj && !current.url && !current.preview) {
      // The copy that will be stored, so an SVG previews as sanitized.
      current.preview = await storedPreviewUrl(current.originFileObj)
    }
    onPhotoFilesChange(next)
  }

  /**
   * Enforces the constraints the hint promises, then returns `false` to keep the
   * file in the list without uploading it. Nothing is sent while the user is
   * still filling the form — no signed URL, no S3 PUT — because they may change
   * the picture, or abandon the flow entirely. `useCreateWorkspace` does the
   * whole upload once, on submit.
   */
  const beforeUpload = async (file: RcFile) => {
    try {
      await validateLogo(file)

      return false
    } catch (rejection) {
      message.error(
        t(
          rejection === 'tooLarge'
            ? 'svgUpload.tooLarge'
            : `workspaces.onboarding.details.iconError.${rejection as string}`,
          { max: LOGO_MAX_DIMENSION, size: MAX_SVG_MB }
        )
      )

      return Upload.LIST_IGNORE
    }
  }

  /**
   * Both states share one right-aligned slot with the tag's trailing margin
   * stripped, so the spinner lands exactly where the verdict will.
   */
  const availabilitySuffix = (
    <span className={styles.availabilitySlot}>
      {availability === 'checking' ? (
        <LoaderCircle size={14} className={styles.availabilitySpinner} />
      ) : availability === 'short' ? (
        <Tag color="warning" style={{ marginInlineEnd: 0 }}>
          {t('workspaces.onboarding.details.addressShort', {
            min: HANDLE_MIN_LENGTH
          })}
        </Tag>
      ) : availability === 'free' ? (
        <Tag color="success" style={{ marginInlineEnd: 0 }}>
          {t('workspaces.onboarding.details.addressFree')}
        </Tag>
      ) : availability === 'taken' ? (
        <Tag color="error" style={{ marginInlineEnd: 0 }}>
          {t('workspaces.onboarding.details.addressTaken')}
        </Tag>
      ) : null}
    </span>
  )

  return (
    <Flex vertical gap={16}>
      <StepHeader title={t('workspaces.onboarding.details.title')} />

      {/* `Form` here is layout only — the values live in `useOnboardingState`
          so they can be persisted and mirrored in the preview. Since the items
          carry no `name`, AntD cannot generate the label/control association
          itself, so each pair is wired explicitly with `htmlFor` + `id`. */}
      <Form layout="vertical" requiredMark={false} component="div">
        {/* No colour picker: `Organization` has no brand-colour field, so the
            tile takes the deterministic avatar colour derived from the name,
            exactly as the sidebar and app rail will render it. */}
        <Form.Item>
          <div className={styles.logoBlock} {...hoverProps('logo')}>
            {/* Once a picture exists it becomes an `Image`, so it can be opened
                full-size — an avatar would only ever show the 52px crop. */}
            {photoPreview ? (
              /* The cover is AntD's own hover overlay, so the eye needs no
                 handler — the overlay itself opens the preview. Only the
                 destructive action is a real control, and it stops the click
                 from reaching the overlay underneath it. */
              <Image
                src={photoPreview}
                alt=""
                width={52}
                height={52}
                className={styles.logoImage}
                preview={{
                  cover: (
                    <Flex align="center" justify="center" gap={8}>
                      <Eye
                        size={15}
                        aria-label={t(
                          'workspaces.onboarding.details.iconPreview'
                        )}
                      />
                      <Button
                        danger
                        type="text"
                        size="small"
                        className={styles.coverAction}
                        icon={<Trash2 size={15} />}
                        aria-label={t(
                          'workspaces.onboarding.details.iconRemove'
                        )}
                        onClick={(event) => {
                          event.stopPropagation()
                          onPhotoFilesChange([])
                        }}
                      />
                    </Flex>
                  )
                }}
              />
            ) : (
              <AvatarWithInitials
                name={state.settledName || 'W'}
                shape="square"
                size={52}
              />
            )}

            <Flex vertical flex={1} style={{ minWidth: 0 }}>
              <Text strong>{t('workspaces.onboarding.details.iconLabel')}</Text>
              <Text type="secondary">
                {t('workspaces.onboarding.details.iconHint', {
                  max: LOGO_MAX_DIMENSION
                })}
              </Text>
            </Flex>

            <Upload
              accept={LOGO_ACCEPT}
              beforeUpload={beforeUpload}
              onChange={onUploadChange}
              maxCount={1}
              showUploadList={false}
            >
              <Button icon={<UploadIcon size={14} />}>
                {photo
                  ? t('workspaces.onboarding.details.iconChange')
                  : t('workspaces.onboarding.details.iconUpload')}
              </Button>
            </Upload>
          </div>
        </Form.Item>

        <Form.Item
          label={t('workspaces.onboarding.details.nameLabel')}
          htmlFor="onboarding-name"
          {...fieldError('name')}
        >
          <Input
            id="onboarding-name"
            size="large"
            autoFocus
            autoComplete="organization"
            value={draft.name}
            placeholder={t('workspaces.onboarding.details.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
            {...focusProps('name')}
          />
        </Form.Item>

        <Form.Item
          label={t('workspaces.onboarding.details.addressLabel')}
          htmlFor="onboarding-address"
          {...fieldError('handle')}
        >
          {/* `Space.Compact` + `Space.Addon` is AntD v6's replacement for the
              deprecated `addonAfter`. */}
          <Space.Compact style={{ width: '100%' }}>
            <Input
              id="onboarding-address"
              size="large"
              value={draft.handle}
              suffix={availabilitySuffix}
              placeholder={t(
                'workspaces.onboarding.details.addressPlaceholder'
              )}
              onChange={(event) => setHandle(event.target.value)}
              {...handleSpotlight}
              // Typing may leave a trailing dash so one can be entered at all;
              // leaving the field is where that gets tidied up.
              onBlur={() => {
                handleSpotlight.onBlur()
                normalizeHandle()
              }}
            />
            <Space.Addon>.{baseHost}</Space.Addon>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label={t('workspaces.onboarding.details.sizeLabel')}
          style={{ marginBottom: 0 }}
          {...fieldError('companySize')}
        >
          {/* No spotlight binding: company size has no counterpart in the
              preview — pointing it at the Members stat would imply the two are
              connected, and they are not. */}
          <Segmented
            block
            className={styles.sizeSegmented}
            value={draft.companySize ?? ''}
            options={COMPANY_SIZES.map((size) => {
              const Icon = COMPANY_SIZE_ICONS[size]

              return {
                value: size,
                icon: <Icon size={14} />,
                label: t(`workspaces.onboarding.companySize.${size}`)
              }
            })}
            onChange={(size) => patch({ companySize: size as CompanySize })}
          />
        </Form.Item>
      </Form>
    </Flex>
  )
}
